/**
 * SyncEngine — the integration layer that binds the pure sync modules to a real
 * media element. The app forwards DOM media events into the onMedia* methods and
 * forwards RoomClient events into the applyRemote / on* methods; the engine owns
 * the loops (drift eval, heartbeat, barrier) and decides what to do to the video
 * and what to broadcast.
 *
 * Broadcast-feedback safety: when the engine itself changes the media (applying
 * a remote control, a drift seek, or a barrier resume) it raises suppression
 * flags so the resulting DOM play/pause/seeked events are NOT re-broadcast.
 */
import {
  initialState,
  TIMING,
  type BufferStateMsg,
  type ControlMsg,
  type HeartbeatMsg,
  type PlaybackState,
  type ResumeAtMsg,
} from "../shared/protocol.ts";
import { applyControl, makeControl, targetPositionS } from "../sync/playbackState.ts";
import { DriftController } from "../sync/drift.ts";
import { BufferBarrier } from "../sync/bufferBarrier.ts";
import type { Scheduler } from "../sync/roomClient.ts";

export interface MediaLike {
  currentTime: number;
  paused: boolean;
  playbackRate: number;
  readyState: number;
  play(): Promise<void> | void;
  pause(): void;
}

export interface RoomTransport {
  clock: { sharedNow(): number; rttHalfMs: number; offset: number };
  isReference: boolean;
  recentMsgIds: ReadonlySet<string>;
  sendControl(m: ControlMsg): void;
  sendHeartbeat(m: HeartbeatMsg): void;
  sendBufferState(m: BufferStateMsg): void;
  sendResumeAt(m: ResumeAtMsg): void;
}

export interface EngineStatus {
  state: PlaybackState;
  selfBuffering: boolean;
  peerBuffering: boolean;
  barrierHolding: boolean;
  offsetMs: number;
  rttHalfMs: number;
}

export interface SyncEngineOptions {
  media: MediaLike;
  transport: RoomTransport;
  selfId: string;
  scheduler: Scheduler;
  now: () => number; // monotonic ms
  uuid: () => string;
  onStatus?: (s: EngineStatus) => void;
  onWatchdog?: () => void;
  /** Fired when a programmatic play() was blocked (autoplay policy) — the UI
   * should invite the user to click play. */
  onNeedGesture?: () => void;
}

export class SyncEngine {
  private readonly o: SyncEngineOptions;
  private state: PlaybackState;
  private readonly drift = new DriftController();
  private readonly barrier = new BufferBarrier();

  // Precise echo suppression: when the engine programmatically mutates the media
  // it expects exactly one matching DOM event back (a programmatic pause emits a
  // `pause`, a seek emits `seeked`, etc.). We consume that one echo per type so a
  // GENUINE user action of a DIFFERENT type in the same instant is never dropped
  // (a real `play` is not an echo of a programmatic `pause`). A safety deadline
  // clears stale expectations if an expected echo never arrives.
  private readonly expect = { play: 0, pause: 0, seek: 0 };
  private expectClearAt = 0;
  private needsGesture = false;

  private selfBuffering = false;
  private peerBuffering = false;
  private peerConnected = false;
  // null = we have never heard a position from the peer. Do NOT use 0 as the
  // "unknown" sentinel: 0 is a valid position (start of the movie).
  private peerPositionS: number | null = null;
  private lastProgressMs = 0;
  private lastMediaTime = 0;

  private driftTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private resumeTimer: number | null = null;
  /** While set, ignore a (stale, in-flight) peer buffering=true so it can't
   * cancel a resume we just committed to. */
  private resumeCommitUntil = 0;

  constructor(opts: SyncEngineOptions) {
    this.o = opts;
    this.state = initialState(opts.transport.clock.sharedNow());
  }

  // ── lifecycle ──────────────────────────────────────────────────────────────
  start(): void {
    this.lastProgressMs = this.o.now();
    this.lastMediaTime = this.o.media.currentTime;
    this.driftTimer = this.o.scheduler.setInterval(() => this.tickDrift(), TIMING.DRIFT_EVAL_MS);
    this.heartbeatTimer = this.o.scheduler.setInterval(() => this.tickHeartbeat(), TIMING.HEARTBEAT_MS);
  }

  stop(): void {
    if (this.driftTimer !== null) this.o.scheduler.clearInterval(this.driftTimer);
    if (this.heartbeatTimer !== null) this.o.scheduler.clearInterval(this.heartbeatTimer);
    if (this.resumeTimer !== null) this.o.scheduler.clearTimeout(this.resumeTimer);
    this.driftTimer = this.heartbeatTimer = this.resumeTimer = null;
  }

  getState(): PlaybackState {
    return this.state;
  }

  private expectEcho(kind: "play" | "pause" | "seek"): void {
    this.expect[kind]++;
    this.expectClearAt = this.o.now() + 500; // stale expectations expire
  }

  /** Returns true (and consumes one) if this event is a programmatic echo. */
  private consumeEcho(kind: "play" | "pause" | "seek"): boolean {
    if (this.o.now() > this.expectClearAt) {
      this.expect.play = this.expect.pause = this.expect.seek = 0;
    }
    if (this.expect[kind] > 0) {
      this.expect[kind]--;
      return true;
    }
    return false;
  }

  /** Programmatic seek that emits a `seeked` echo (only when it actually moves). */
  private seekMedia(target: number): void {
    if (Math.abs(this.o.media.currentTime - target) <= 0.25) return;
    this.expectEcho("seek");
    this.o.media.currentTime = target;
    this.drift.suppressUntil(this.o.now(), TIMING.SEEK_SETTLE_MS);
  }

  private pauseMedia(): void {
    if (this.o.media.paused) return;
    this.expectEcho("pause");
    this.o.media.pause();
  }

  /** Autoplay-safe play(). If the browser blocks it (no user gesture), we must
   * NOT keep believing we're playing — otherwise drift/heartbeat chase a video
   * the browser is holding paused. Reflect that a gesture is needed instead. */
  private playMedia(): void {
    this.needsGesture = false;
    if (!this.o.media.paused) return;
    this.expectEcho("play");
    const p = this.o.media.play();
    if (p && typeof (p as Promise<void>).then === "function") {
      (p as Promise<void>).catch(() => {
        this.needsGesture = true;
        this.expect.play = Math.max(0, this.expect.play - 1); // no echo will arrive
        this.o.onNeedGesture?.();
      });
    }
  }

  /** A genuine user action (or an applied remote control) supersedes any buffer
   * wait: cancel the barrier hold + pending resume so it can't clobber intent. */
  private cancelBarrier(): void {
    if (this.resumeTimer !== null) {
      this.o.scheduler.clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
    this.barrier.reset();
    // If we were buffering, tell the peer we've cleared — otherwise they stay
    // holding forever on our stale buffering=true.
    if (this.selfBuffering) {
      this.selfBuffering = false;
      this.o.transport.sendBufferState({
        t: "buffer_state",
        originId: this.o.selfId,
        buffering: false,
        atVideoTimeS: this.o.media.currentTime,
        atSharedMs: this.o.transport.clock.sharedNow(),
      });
    }
  }

  get gestureRequired(): boolean {
    return this.needsGesture;
  }

  private emitStatus(): void {
    this.o.onStatus?.({
      state: this.state,
      selfBuffering: this.selfBuffering,
      peerBuffering: this.peerBuffering,
      barrierHolding: this.barrier.currentPhase !== "idle",
      offsetMs: this.o.transport.clock.offset,
      rttHalfMs: this.o.transport.clock.rttHalfMs,
    });
  }

  // ── local (user) media events ───────────────────────────────────────────────
  onMediaPlay(): void {
    this.needsGesture = false; // the user just provided the gesture
    if (this.consumeEcho("play")) return; // our own programmatic play
    const wasHolding = this.barrier.currentPhase !== "idle";
    this.cancelBarrier();
    // If the room is ALREADY playing and we were just catching up (e.g. an
    // unblocked autoplay, or resuming after a hold), don't rewind the peer to our
    // stale position — snap ourselves forward to the live target and follow.
    if (this.peerConnected && !this.state.paused && !wasHolding) {
      this.applyStateToMedia(this.state);
      return;
    }
    this.broadcastControl("play", { paused: false });
  }

  onMediaPause(): void {
    if (this.consumeEcho("pause")) return; // our own programmatic pause (barrier/apply)
    this.cancelBarrier();
    this.broadcastControl("pause", { paused: true });
  }

  onMediaSeeked(): void {
    if (this.consumeEcho("seek")) return; // our own programmatic seek (drift/resume/apply)
    // During a buffer hold the element is transiently auto-paused; broadcast the
    // LOGICAL play/pause intent, not the transient media.paused.
    const wasHolding = this.barrier.currentPhase !== "idle";
    this.cancelBarrier();
    const paused = wasHolding ? this.state.paused : this.o.media.paused;
    this.broadcastControl("seek", { paused });
    if (!paused && this.o.media.paused) this.playMedia(); // resume out of the hold
    this.drift.suppressUntil(this.o.now(), TIMING.SEEK_SETTLE_MS);
  }

  private broadcastControl(action: "play" | "pause" | "seek", opts: { paused: boolean }): void {
    const at = this.o.transport.clock.sharedNow();
    const ctrl = makeControl(this.state, action, {
      positionS: this.o.media.currentTime,
      atSharedMs: at,
      originId: this.o.selfId,
      msgId: this.o.uuid(),
      paused: opts.paused,
      rate: this.state.rate,
    });
    this.state = {
      epoch: ctrl.epoch,
      positionS: ctrl.positionS,
      refSharedMs: ctrl.atSharedMs,
      paused: ctrl.paused,
      rate: ctrl.rate,
      originId: ctrl.originId,
    };
    this.o.transport.sendControl(ctrl);
    this.emitStatus();
  }

  // ── remote events ────────────────────────────────────────────────────────────
  applyRemoteControl(msg: ControlMsg): void {
    this.peerConnected = true; // a control from the peer proves they're connected
    const res = applyControl(this.state, msg, this.o.selfId, this.o.transport.recentMsgIds);
    if (!res.applied) return;
    // The peer's explicit action supersedes any local buffer wait.
    this.cancelBarrier();
    this.state = res.state;
    this.applyStateToMedia(this.state);
    this.emitStatus();
  }

  onWelcome(state: PlaybackState, peerConnected = false): void {
    this.peerConnected = peerConnected;
    this.state = state;
    this.applyStateToMedia(state);
    this.emitStatus();
  }

  onSnapshot(state: PlaybackState): void {
    // Only adopt if newer than what we have (snapshot may be a reconnect echo).
    if (state.epoch >= this.state.epoch) {
      this.state = state;
      this.applyStateToMedia(state);
      this.emitStatus();
    }
  }

  onRemoteHeartbeat(msg: HeartbeatMsg): void {
    // Ignore a stale in-flight message from a peer that has already left — it
    // must NOT resurrect presence (that deadlocks the barrier). Presence is
    // authoritative and always arrives before any relayed peer message on join.
    if (!this.peerConnected) return;
    this.peerPositionS = msg.videoTimeS;
    this.peerBuffering = msg.buffering;
  }

  onRemoteBufferState(msg: BufferStateMsg): void {
    if (!this.peerConnected) return; // stale post-departure message — ignore
    // During a resume commit, ignore a stale in-flight buffering=true that would
    // otherwise cancel the resume and strand us paused (falling to the failsafe,
    // which diverges from the reference).
    if (msg.buffering && this.o.now() < this.resumeCommitUntil) return;
    this.peerBuffering = msg.buffering;
    if (msg.buffering) this.peerPositionS = msg.atVideoTimeS;
    this.tickBarrier();
  }

  onRemoteResumeAt(msg: ResumeAtMsg): void {
    // The reference has decided to resume — commit to it (same frame as them).
    this.peerBuffering = false;
    this.resumeCommitUntil =
      this.o.now() + Math.max(0, msg.atSharedMs - this.o.transport.clock.sharedNow()) + 400;
    const action = this.barrier.onResumeAt({ positionS: msg.positionS, atSharedMs: msg.atSharedMs });
    if (action.type === "schedule-resume") this.scheduleResume(action.positionS, action.atSharedMs);
  }

  onPresence(peerConnected: boolean): void {
    this.peerConnected = peerConnected;
    if (!peerConnected) {
      // The peer is gone — nobody to wait for. Release any buffer hold and, if
      // we were auto-paused mid-playback waiting for them, resume solo (the user
      // already gestured, so this is not a fresh autoplay). This prevents the
      // "friend closed their tab while buffering → I'm frozen forever" deadlock.
      const wasHolding = this.barrier.currentPhase !== "idle";
      this.peerBuffering = false;
      this.peerPositionS = null;
      this.cancelBarrier();
      if (wasHolding && !this.state.paused && this.o.media.paused) {
        this.playMedia();
      }
    } else {
      this.tickBarrier();
    }
    this.emitStatus();
  }

  private applyStateToMedia(state: PlaybackState): void {
    // Project by wall-clock ONLY when a live peer is present (their timestamp is
    // recent). Solo, we adopt the raw stored position — projecting a stale
    // "playing since <old time>" state would jump the movie minutes ahead.
    const target = this.peerConnected
      ? targetPositionS(state, this.o.transport.clock.sharedNow())
      : state.positionS;
    // seekMedia / pauseMedia / playMedia each register exactly one expected echo
    // ONLY when they actually mutate, so a genuine user action of any type is
    // never swallowed.
    this.seekMedia(target);
    // Solo never auto-plays (don't fight the autoplay policy); it holds paused
    // until the user presses play, which re-syncs from wherever they are.
    if (state.paused || !this.peerConnected) this.pauseMedia();
    else this.playMedia();
  }

  // ── loops ────────────────────────────────────────────────────────────────────
  private tickDrift(): void {
    // Drift correction is ONLY for staying locked to a live peer. Never run it:
    //  - solo (no peer to sync with — just watch normally)
    //  - logically paused
    //  - the media element itself is paused (user/autoplay/barrier) — chasing a
    //    paused element is what caused "chunky forward-seeking while paused"
    //  - not enough data buffered (let the barrier / native player handle it)
    //  - the barrier is mid-hold/resume
    if (
      !this.peerConnected ||
      this.state.paused ||
      this.o.media.paused ||
      this.o.media.readyState < 3 ||
      this.barrier.currentPhase !== "idle"
    ) {
      return;
    }
    const sharedNow = this.o.transport.clock.sharedNow();
    const target = targetPositionS(this.state, sharedNow);
    const errS = this.o.media.currentTime - target;
    const corr = this.drift.evaluate({
      errS,
      rttHalfMs: this.o.transport.clock.rttHalfMs,
      targetS: target + this.o.transport.clock.rttHalfMs / 1000,
      paused: false,
      nowMs: this.o.now(),
    });
    if (corr.mode === "nudge") {
      this.o.media.playbackRate = corr.rate;
    } else if (corr.mode === "seek") {
      this.o.media.playbackRate = 1;
      this.seekMedia(corr.seekToS);
    } else {
      this.o.media.playbackRate = 1;
    }
  }

  private tickHeartbeat(): void {
    this.detectStall();
    const hb: HeartbeatMsg = {
      t: "heartbeat",
      originId: this.o.selfId,
      epoch: this.state.epoch,
      videoTimeS: this.o.media.currentTime,
      atSharedMs: this.o.transport.clock.sharedNow(),
      paused: this.state.paused,
      buffering: this.selfBuffering,
      readyState: this.o.media.readyState,
    };
    this.o.transport.sendHeartbeat(hb);
    this.tickBarrier();
    this.emitStatus();
  }

  private detectStall(): void {
    const now = this.o.now();
    const t = this.o.media.currentTime;
    // Clear a buffering flag the moment we have enough data — even if the media
    // is currently held paused by the buffer barrier (so currentTime is NOT
    // advancing and no `playing` event will fire). Without this poll a barrier
    // hold deadlocks: paused video never reports progress, so buffering never
    // clears, so the barrier never resumes.
    if (this.selfBuffering && this.o.media.readyState >= 3) {
      this.setSelfBuffering(false);
    }
    if (!this.state.paused) {
      if (t > this.lastMediaTime + 0.01) {
        this.lastMediaTime = t;
        this.lastProgressMs = now;
      } else if (now - this.lastProgressMs > TIMING.STALL_NO_PROGRESS_MS && this.o.media.readyState < 3) {
        this.setSelfBuffering(true);
      }
    } else {
      this.lastMediaTime = t;
      this.lastProgressMs = now;
    }
  }

  /** Media element event: readyState dropped / waiting for data. */
  mediaWaiting(): void {
    this.setSelfBuffering(true);
  }

  /** Media element event: playback resumed with data. */
  mediaPlaying(): void {
    if (this.o.media.readyState >= 3) this.setSelfBuffering(false);
  }

  private setSelfBuffering(v: boolean): void {
    if (this.selfBuffering === v) return;
    this.selfBuffering = v;
    // Edge-triggered broadcast (don't wait for the heartbeat tick).
    this.o.transport.sendBufferState({
      t: "buffer_state",
      originId: this.o.selfId,
      buffering: v,
      atVideoTimeS: this.o.media.currentTime,
      atSharedMs: this.o.transport.clock.sharedNow(),
    });
    this.tickBarrier();
  }

  private tickBarrier(): void {
    // The barrier only makes sense during PLAYBACK with a peer to wait for.
    // Watching solo, or while logically paused, buffering is just normal
    // buffering — never auto-pause / show the "waiting for guest" overlay.
    const active = this.peerConnected && !this.state.paused;
    const action = this.barrier.update({
      selfBuffering: active && this.selfBuffering,
      peerBuffering: active && this.peerBuffering,
      selfPositionS: this.o.media.currentTime,
      peerPositionS: this.peerPositionS ?? this.o.media.currentTime,
      isReference: this.o.transport.isReference,
      nowSharedMs: this.o.transport.clock.sharedNow(),
    });
    switch (action.type) {
      case "hold":
        this.pauseMedia();
        break;
      case "broadcast-resume":
        this.o.transport.sendResumeAt({
          t: "resume_at",
          epoch: this.state.epoch + 1,
          originId: this.o.selfId,
          positionS: action.positionS,
          atSharedMs: action.atSharedMs,
        });
        this.scheduleResume(action.positionS, action.atSharedMs);
        break;
      case "schedule-resume":
        this.scheduleResume(action.positionS, action.atSharedMs);
        break;
      case "watchdog":
        this.o.onWatchdog?.();
        break;
    }
  }

  private scheduleResume(positionS: number, atSharedMs: number): void {
    if (this.resumeTimer !== null) this.o.scheduler.clearTimeout(this.resumeTimer);
    const delay = Math.max(0, atSharedMs - this.o.transport.clock.sharedNow());
    this.resumeTimer = this.o.scheduler.setTimeout(() => {
      this.resumeTimer = null;
      this.barrier.onResumed();
      // If the room was paused (by a user) while we waited, do NOT un-pause it.
      if (this.state.paused) {
        this.emitStatus();
        return;
      }
      this.seekMedia(positionS);
      this.state = { ...this.state, positionS, refSharedMs: this.o.transport.clock.sharedNow() };
      this.playMedia();
      this.emitStatus();
    }, delay);
  }
}
